import { useQuery } from '@tanstack/react-query';
import { getEquipment, getAllBookings, getCategories } from '../../services/supabase';
import { Button } from '../../components/ui/Button';

const AdminDashboard = () => {
    // Получаем данные для информационных карточек
    const { data: equipment } = useQuery({
        queryKey: ['equipment'],
        queryFn: async () => {
            const { data, error } = await getEquipment();
            if (error) throw error;
            return data;
        },
    });

    const { data: bookingsData, isLoading: isLoadingBookings, error: bookingsError } = useQuery({
        queryKey: ['admin-bookings'],
        queryFn: async () => {
            console.log('Выполняю запрос бронирований для админ-панели');
            const { data, error } = await getAllBookings();
            if (error) {
                console.error('Ошибка получения бронирований в админ-панели:', error);
                throw error;
            }
            console.log('Получены данные бронирований:', data ? data.length : 0, 'записей');
            return data || [];
        },
        staleTime: 0, // Отключаем кеширование для этого запроса
        refetchOnMount: true, // Всегда обновлять при монтировании компонента
        refetchOnWindowFocus: true, // Обновлять при фокусе окна
    });

    const { data: categories } = useQuery({
        queryKey: ['categories'],
        queryFn: async () => {
            const { data, error } = await getCategories();
            if (error) throw error;
            return data;
        },
    });

    // Счетчики для статистики
    const equipmentCount = equipment?.length || 0;
    const availableEquipment = equipment?.filter(item => item.status === 'available').length || 0;
    const bookingsCount = bookingsData?.length || 0;
    const categoriesCount = categories?.length || 0;

    // Отладочная информация о статусе загрузки бронирований
    if (isLoadingBookings) {
        console.log('Загрузка данных бронирований...');
    }

    if (bookingsError) {
        console.error('Ошибка загрузки бронирований:', bookingsError);
    }

    // Информация о количестве бронирований
    console.log('Количество бронирований для отображения:', bookingsCount);

    // Иконки для карточек
    const dashboardCards = [
        {
            title: 'Оборудование',
            value: equipmentCount,
            description: `${availableEquipment} доступно`,
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
            ),
            link: '/admin/equipment',
            color: 'bg-primary-50 text-primary-600',
            buttonColor: 'text-primary-600 hover:text-primary-700',
            actions: [
                { text: 'Управление', link: '/admin/equipment' },
                { text: 'Добавить', link: '/admin/equipment/new' }
            ]
        },
        {
            title: 'Бронирования',
            value: bookingsCount,
            description: 'Всего бронирований',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            ),
            link: '/admin/bookings',
            color: 'bg-blue-50 text-blue-600',
            buttonColor: 'text-blue-600 hover:text-blue-700',
            actions: [
                { text: 'Управление', link: '/admin/bookings' }
            ]
        },
        {
            title: 'Категории',
            value: categoriesCount,
            description: 'Категорий оборудования',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
            ),
            link: '/admin/categories',
            color: 'bg-green-50 text-green-600',
            buttonColor: 'text-green-600 hover:text-green-700',
            actions: [
                { text: 'Управление', link: '/admin/categories' },
                { text: 'Добавить', link: '/admin/categories/new' }
            ]
        },
        {
            title: 'Скидки',
            value: 'По ролям',
            description: 'Управление скидками',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            link: '/admin/discounts',
            color: 'bg-amber-50 text-amber-600',
            buttonColor: 'text-amber-600 hover:text-amber-700',
            actions: [
                { text: 'Управление', link: '/admin/discounts' }
            ]
        },
        {
            title: 'Уведомления',
            value: 'Telegram',
            description: 'Тестирование уведомлений',
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
            ),
            link: '/admin/test-notifications',
            color: 'bg-purple-50 text-purple-600',
            buttonColor: 'text-purple-600 hover:text-purple-700',
            actions: [
                { text: 'Тестировать', link: '/admin/test-notifications' }
            ]
        },
    ];

    return (
        <div className="max-w-6xl mx-auto px-4 py-6">
            {/* Заголовок с навигационной цепочкой */}
            <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                    <h1 className="text-2xl md:text-3xl font-semibold text-gray-800">Панель администратора</h1>
                    <Button to="/" variant="outline" size="sm" className="hidden md:inline-flex">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        Вернуться на сайт
                    </Button>
                </div>
                <p className="text-gray-500">
                    Управление сервисом аренды оборудования
                </p>
            </div>

            {/* Карточки с ключевыми метриками */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {dashboardCards.map((card, index) => (
                    <div
                        key={index}
                        className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 transition-all hover:shadow-md"
                    >
                        <div className="flex items-center">
                            <div className={`${card.color} p-3 rounded-full mr-4`}>
                                {card.icon}
                            </div>
                            <div>
                                <h2 className="text-lg font-medium text-gray-700">{card.title}</h2>
                                <div className="text-3xl font-semibold mt-1">{card.value}</div>
                                <p className="text-gray-500 text-sm">{card.description}</p>
                            </div>
                        </div>
                        
                        <div className="mt-6 grid grid-cols-2 gap-2">
                            {card.actions.map((action, actionIndex) => (
                                <Button 
                                    key={actionIndex}
                                    to={action.link}
                                    variant={actionIndex === 0 ? "secondary" : "outline"}
                                    size="sm"
                                    className="w-full justify-center"
                                >
                                    {action.text}
                                </Button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {/* Мини-навигация для мобильных устройств */}
            <div className="md:hidden mb-6">
                <Button to="/" variant="outline" className="w-full justify-center" size="sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Вернуться на сайт
                </Button>
            </div>
        </div>
    );
};

export default AdminDashboard; 